import { AudioTrack, useTracks, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { coreStore } from "@/lib/stores/core-store";
import { CustomParticipantTile } from "./custom-participant-tile";

const GAP = 8;
const ASPECT_RATIO = 16 / 9;

function computeOptimalGrid(
	n: number,
	containerWidth: number,
	containerHeight: number
): { layout: number[]; tileWidth: number; tileHeight: number } {
	if (n <= 0) return { layout: [], tileWidth: 0, tileHeight: 0 };

	let bestArea = 0;
	let bestCols = 1;
	let bestTileW = 0;
	let bestTileH = 0;

	for (let cols = 1; cols <= n; cols++) {
		const rows = Math.ceil(n / cols);
		const maxW = (containerWidth - GAP * (cols - 1)) / cols;
		const hFromW = maxW / ASPECT_RATIO;
		const maxH = (containerHeight - GAP * (rows - 1)) / rows;
		const wFromH = maxH * ASPECT_RATIO;
		const tileW = Math.min(maxW, wFromH);
		const tileH = Math.min(hFromW, maxH);
		const area = tileW * tileH;
		if (tileW > 0 && tileH > 0 && area > bestArea) {
			bestArea = area;
			bestCols = cols;
			bestTileW = tileW;
			bestTileH = tileH;
		}
	}

	const rows = Math.ceil(n / bestCols);
	const layout: number[] = [];
	let remaining = n;
	for (let r = 0; r < rows; r++) {
		const rowSize = Math.min(bestCols, remaining);
		layout.push(rowSize);
		remaining -= rowSize;
	}

	return { layout, tileWidth: bestTileW, tileHeight: bestTileH };
}

type BroadcastVideoGridProps = {
	variant?: "full" | "compact";
	volume?: number;
};

export function BroadcastVideoGrid({
	variant = "full",
	volume = 1,
}: BroadcastVideoGridProps) {
	const broadcast = useStore(coreStore, (state) => state.broadcast);

	const trackSources = useMemo(
		() => [
			{ source: Track.Source.Camera, withPlaceholder: true },
			{ source: Track.Source.ScreenShare, withPlaceholder: false },
		],
		[]
	);

	const allTracks = useTracks(trackSources, { onlySubscribed: false });

	const speakerTracks = useMemo(
		() =>
			allTracks.filter((track) => {
				const permissions = track.participant.permissions;
				const participantId = track.participant.identity;

				const isBroadcaster = participantId === broadcast?.startedBy;
				const isInvitedSpeaker =
					broadcast?.invitedSpeakers?.includes(participantId) ?? false;

				return (
					permissions?.canPublish === true &&
					(isBroadcaster || isInvitedSpeaker)
				);
			}),
		[allTracks, broadcast?.startedBy, broadcast?.invitedSpeakers]
	);

	const screenShareTrack = useMemo(
		() =>
			speakerTracks.find((track) => track.source === Track.Source.ScreenShare),
		[speakerTracks]
	);

	const cameraTrack = useMemo(
		() => speakerTracks.find((track) => track.source === Track.Source.Camera),
		[speakerTracks]
	);

	const pipSize =
		variant === "compact"
			? "max-h-16 max-w-24 bottom-2 right-2"
			: "max-h-32 max-w-44 bottom-4 right-4";

	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
	const nodeRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	const measure = useCallback(() => {
		const el = nodeRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		setContainerSize((prev) => {
			if (prev.width === rect.width && prev.height === rect.height) return prev;
			return { width: rect.width, height: rect.height };
		});
	}, []);

	const containerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			nodeRef.current = node;
			if (!node) return;
			const rect = node.getBoundingClientRect();
			setContainerSize({ width: rect.width, height: rect.height });
			observerRef.current = new ResizeObserver(() => measure());
			observerRef.current.observe(node);
		},
		[measure]
	);

	useEffect(() => {
		const el = nodeRef.current;
		const win = el?.ownerDocument.defaultView ?? window;
		const onResize = () => measure();
		win.addEventListener("resize", onResize);
		return () => {
			win.removeEventListener("resize", onResize);
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [measure]);

	const { layout, tileWidth, tileHeight } = useMemo(() => {
		if (containerSize.width === 0 || containerSize.height === 0) {
			return { layout: [], tileWidth: 0, tileHeight: 0 };
		}
		return computeOptimalGrid(
			speakerTracks.length,
			containerSize.width,
			containerSize.height
		);
	}, [speakerTracks.length, containerSize]);

	const audioTrackSources = useMemo(
		() => [Track.Source.Microphone, Track.Source.ScreenShareAudio],
		[]
	);

	const audioTracks = useTracks(audioTrackSources, {
		onlySubscribed: false,
	}).filter((ref) => !ref.participant.isLocal);

	const audioRenderer = (
		<div style={{ display: "none" }}>
			{audioTracks.map((trackRef) => (
				<AudioTrack
					key={`${trackRef.participant.sid}-${trackRef.source}`}
					trackRef={trackRef}
					volume={volume}
				/>
			))}
		</div>
	);

	if (screenShareTrack) {
		return (
			<div className="relative h-full w-full bg-muted">
				<div className="[&_.lk-participant-tile.lk-participant-tile]:!border-0 absolute inset-0 [&_.lk-participant-metadata]:hidden [&_.lk-participant-name]:hidden [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-contain">
					<CustomParticipantTile trackRef={screenShareTrack} />
				</div>
				{cameraTrack?.publication?.track &&
					!cameraTrack.publication.isMuted && (
						<div
							className={`[&_.lk-participant-tile.lk-participant-tile]:!border-0 absolute z-10 overflow-hidden rounded-lg border-2 border-background shadow-lg [&_.lk-participant-metadata]:hidden [&_.lk-participant-name]:hidden [&_video]:block ${pipSize}`}
						>
							<VideoTrack trackRef={cameraTrack} />
						</div>
					)}
				{audioRenderer}
			</div>
		);
	}

	let trackIndex = 0;
	const rows = layout.map((cols, rowIdx) => {
		const rowTracks = speakerTracks.slice(trackIndex, trackIndex + cols);
		trackIndex += cols;
		return (
			<div
				className="flex items-center justify-center"
				key={rowIdx}
				style={{ gap: GAP }}
			>
				{rowTracks.map((track) => (
					<div
						className="overflow-hidden rounded-lg bg-muted"
						key={`${track.participant.sid}-${track.source}`}
						style={{
							width: tileWidth,
							height: tileHeight,
						}}
					>
						<div className="h-full w-full [&_.lk-participant-name]:text-white [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_.lk-participant-tile]:max-w-none">
							<CustomParticipantTile trackRef={track} />
						</div>
					</div>
				))}
			</div>
		);
	});

	return (
		<div
			className="relative h-full w-full overflow-hidden bg-muted"
			ref={containerRef}
		>
			<div
				className="absolute inset-0 flex flex-col items-center justify-center"
				style={{ gap: GAP }}
			>
				{rows}
			</div>
			{audioRenderer}
		</div>
	);
}
